import { useState } from 'react';
import { Book, Globe, Clock, FileCheck, Star, Award, Shield, UserCheck } from 'lucide-react';
import HighlightText from '../components/core/HomePage/HighlightText';
import Footer from '../components/common/Footer';

export default function DissertationService() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [academicLevel, setAcademicLevel] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log({ name, email, academicLevel, subject, message });
  };

  return (
    <div className="min-h-screen bg-richblue-5 text-richblue-700">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-richblue-200 to-richblue-100 text-richblue-700 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Expert Dissertation Writing Services</h1>
          <p className="text-xl text-richblue-500">
            Transform your academic journey with our premium dissertation writing assistance crafted by PhD-qualified experts
          </p>
        </div>
      </div>

      {/* Overview Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Comprehensive Dissertation <HighlightText text={"Support"} /></h2>
            <p className="text-richblue-500 mb-8 text-lg">
              Crafting a dissertation represents the pinnacle of your academic journey. Our specialized dissertation writing services provide comprehensive support through every phase of this challenging process. From initial concept development to final defense preparation, our team of PhD-qualified experts delivers exceptional quality across all disciplines.
            </p>
            
            <div className="bg-richblue-5 p-8 rounded-lg shadow-md border border-richblue-300 mb-12">
              <h3 className="text-2xl font-bold mb-4 text-richblue-600">Why Students Choose Our Dissertation Services</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Expert PhD-level writers with field-specific expertise</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Comprehensive research using advanced academic databases</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Custom methodology development tailored to your research</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Rigorous adherence to academic standards and formatting</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Advanced statistical analysis and data interpretation</p>
                </div>
                <div className="flex items-start">
                  <div className="text-richblue-400 mr-3 mt-1">✓</div>
                  <p className="text-richblue-500">Unlimited revisions until complete satisfaction</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-16 bg-richblue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Dissertation <HighlightText text={"Services"} /></h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Book className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Full Dissertation Writing</h3>
              <p className="text-richblue-500">
                Complete dissertation development from proposal to defense, including all chapters, literature review, methodology, results, and conclusion.
              </p>
            </div>

            {/* Service Card 2 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <FileCheck className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Chapter-by-Chapter Assistance</h3>
              <p className="text-richblue-500">
                Focused support for specific dissertation chapters, allowing you to collaborate on select portions while maintaining full control of your project.
              </p>
            </div>

            {/* Service Card 3 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Globe className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Literature Review</h3>
              <p className="text-richblue-500">
                Comprehensive analysis of current research in your field, identifying gaps, trends, and theoretical frameworks to position your research effectively.
              </p>
            </div>

            {/* Service Card 4 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Star className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Research Methodology</h3>
              <p className="text-richblue-500">
                Expert development of appropriate research methodologies, including qualitative, quantitative, and mixed methods approaches with detailed justification.
              </p>
            </div>

            {/* Service Card 5 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Award className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Data Analysis</h3>
              <p className="text-richblue-500">
                Professional statistical analysis and data interpretation using SPSS, R, NVivo, and other specialized software to generate meaningful results and visualizations.
              </p>
            </div>

            {/* Service Card 6 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                <Shield className="h-8 w-8 text-richblue-400" />
              </div>
              <h3 className="text-xl font-semibold text-richblue-600 mb-2">Editing & Proofreading</h3>
              <p className="text-richblue-500">
                Thorough review of your dissertation for clarity, coherence, grammar, formatting, and adherence to academic standards and citation styles.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <div className="bg-richblue-5 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Dissertation Development <HighlightText text={"Process"} /></h2>
          
          <div className="max-w-4xl mx-auto">
            {/* Process Step 1 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Consultation & Topic Refinement</h3>
                <p className="text-richblue-500">
                  We begin with an in-depth consultation to understand your research interests, academic goals, and university requirements. Our experts help refine your dissertation topic to ensure it's original, significant, and achievable within your timeframe.
                </p>
              </div>
            </div>

            {/* Process Step 2 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Comprehensive Research & Planning</h3>
                <p className="text-richblue-500">
                  Our PhD-qualified researchers conduct extensive literature reviews using premier academic databases and develop a detailed research plan with appropriate methodologies tailored to your specific research questions.
                </p>
              </div>
            </div>

            {/* Process Step 3 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Chapter Development & Data Analysis</h3>
                <p className="text-richblue-500">
                  We systematically develop each chapter of your dissertation with regular updates and collaborative feedback. Our statistical experts conduct rigorous data analysis using advanced tools to generate robust findings.
                </p>
              </div>
            </div>

            {/* Process Step 4 */}
            <div className="flex mb-8">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items:center justify-center font-bold text-xl">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Results Interpretation & Discussion</h3>
                <p className="text-richblue-500">
                  We provide comprehensive interpretation of research findings, contextualizing results within existing literature and developing meaningful discussion points that highlight the significance of your contribution.
                </p>
              </div>
            </div>
            
            {/* Process Step 5 */}
            <div className="flex">
              <div className="mr-6">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl">
                  5
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-richblue-600">Finalization & Defense Preparation</h3>
                <p className="text-richblue-500">
                  Following multiple rounds of review and revision, we deliver a polished final dissertation that meets the highest academic standards. We also provide support materials for your dissertation defense, including presentation slides and anticipated questions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Testimonials Section */}
      <div className="py-16 bg-richblue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Success <HighlightText text={"Stories"} /></h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-10 h-10 flex items-center justify-center mr-3">
                  <UserCheck className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-richblue-600">Sarah M.</h4>
                  <p className="text-sm text-richblue-500">PhD in Psychology</p>
                </div>
              </div>
              <p className="text-richblue-500 italic">
                "The dissertation support I received was exceptional. My writer had deep expertise in qualitative research methods and helped me develop a methodology that impressed my committee. The service transformed my dissertation experience from overwhelming to manageable."
              </p>
              <div className="flex text-richblue-400 mt-4">
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-10 h-10 flex items:center justify-center mr-3">
                  <UserCheck className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-richblue-600">Michael T.</h4>
                  <p className="text-sm text-richblue-500">DBA Graduate</p>
                </div>
              </div>
              <p className="text-richblue-500 italic">
                "As a working professional pursuing my doctorate, time was my greatest constraint. The chapter-by-chapter assistance allowed me to maintain momentum while balancing my career demands. The statistical analysis support was particularly valuable."
              </p>
              <div className="flex text-richblue-400 mt-4">
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
            </div>
            
            {/* Testimonial 3 */}
            <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300 hover:bg-richblue-25 transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="bg-richblue-200 text-richblue-900 rounded-full w-10 h-10 flex items-center justify-center mr-3">
                  <UserCheck className="h-5 w-5" />
                </div>
                <div>
                  <h4 className="font-bold text-richblue-600">Jessica L.</h4>
                  <p className="text-sm text-richblue-500">PhD in Education</p>
                </div>
              </div>
              <p className="text-richblue-500 italic">
                "The literature review service helped me navigate the vast amount of research in my field. My writer identified relevant theoretical frameworks I hadn't considered and helped structure my review in a way that perfectly framed my research questions."
              </p>
              <div className="flex text-richblue-400 mt-4">
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
                <Star size={16} fill="currentColor" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Form Section */}
      <div className="bg-richblack-800 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-yellow-50">Begin Your Dissertation Journey</h2>
              <p className="text-richblack-300 mb-6">
                Connect with our dissertation specialists for a personalized consultation. We'll discuss your research goals, timeline, and university requirements to craft a tailored solution.
              </p>
              <div className="bg-richblack-800 p-6 rounded-lg shadow-md border border-richblack-700 mb-6">
                <h3 className="text-xl font-bold mb-4 text-yellow-50">Our Guarantees</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="text-yellow-50 mr-3 mt-1">★</div>
                    <div>
                      <p className="font-semibold text-yellow-50">Originality Guarantee</p>
                      <p className="text-richblack-300 text-sm">Every dissertation is developed from scratch with plagiarism verification</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-yellow-50 mr-3 mt-1">★</div>
                    <div>
                      <p className="font-semibold text-yellow-50">Confidentiality Promise</p>
                      <p className="text-richblack-300 text-sm">Your personal information and project details remain strictly confidential</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <div className="text-yellow-50 mr-3 mt-1">★</div>
                    <div>
                      <p className="font-semibold text-yellow-50">Satisfaction Commitment</p>
                      <p className="text-richblack-300 text-sm">Unlimited revisions until you're completely satisfied with the final product</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="bg-richblack-800 p-6 rounded-lg shadow-md border border-richblack-700">
              <h3 className="text-xl font-bold mb-4 text-yellow-50">Request a Consultation</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-richblack-200 mb-2">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-richblack-700 border border-richblack-600 rounded p-3 text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-richblack-200 mb-2">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-richblack-700 border border-richblack-600 rounded p-3 text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="academicLevel" className="block text-richblack-200 mb-2">Academic Level</label>
                  <select
                    id="academicLevel"
                    value={academicLevel}
                    onChange={(e) => setAcademicLevel(e.target.value)}
                    className="w-full bg-richblack-700 border border-richblack-600 rounded p-3 text-white"
                    required
                  >
                    <option value="">Select Academic Level</option>
                    <option value="Masters">Master's Degree</option>
                    <option value="PhD">PhD</option>
                    <option value="DBA">DBA</option>
                    <option value="EdD">EdD</option>
                    <option value="Other">Other Doctorate</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-richblack-200 mb-2">Research Field</label>
                  <select
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full bg-richblack-700 border border-richblack-600 rounded p-3 text-white"
                    required
                  >
                    <option value="">Select Research Field</option>
                    <option value="Business">Business & Management</option>
                    <option value="Education">Education</option>
                    <option value="Healthcare">Healthcare & Nursing</option>
                    <option value="Psychology">Psychology</option>
                    <option value="Engineering">Engineering</option>
                    <option value="ComputerScience">Computer Science</option>
                    <option value="SocialSciences">Social Sciences</option>
                    <option value="Arts">Arts & Humanities</option>
                    <option value="Law">Law</option>
                    <option value="Other">Other Field</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-richblack-200 mb-2">Project Details</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Please describe your dissertation topic, requirements, timeline, and any specific assistance needed."
                    className="w-full bg-richblack-700 border border-richblack-600 rounded p-3 text-white h-32"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full bg-yellow-50 text-richblack-900 font-semibold py-3 px-6 rounded-md hover:bg-yellow-100 transition-all duration-200"
                >
                  Request Consultation
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}